import React, { useEffect, useRef, useState } from "react";
import "./DiwaliCelebrationOverlay.css";

// Auto-expires at the end of 13 November 2026 (23:59:59.999)
export const DIWALI_EXPIRY_DATE = new Date(2026, 10, 13, 23, 59, 59, 999).getTime();
const EXPIRY_DATE = DIWALI_EXPIRY_DATE;

const TWO_PI = Math.PI * 2;
const GOLD = "#fbbf24";

// 1. Original vibrant colors for login celebration (User's beloved design)
const ORIGINAL_ROCKET_TRAILS = [
  "#ffd200", // 1. Yellow
  "#ff7700", // 2. Orange
  "#ffea00", // 3. Bright Golden Yellow
  "#ff3864", // 4. Coral / Red Pink
  "#00d2ff", // 5. Cyan / Sky Blue
  "#a855f7", // 6. Violet / Purple
  "#10b981", // 7. Emerald Green
  "#ec4899", // 8. Hot Pink / Magenta
];

const ORIGINAL_BURST_COLORS = [
  "#ffd200", // Gold Yellow
  "#ff7700", // Orange
  "#00d2ff", // Cyan
  "#ff3864", // Coral
  "#a855f7", // Violet
  "#10b981", // Emerald
  "#ec4899", // Pink
  "#ffffff", // White
  "#38bdf8", // Sky Blue
  "#facc15", // Amber
];

// 2. Soft, airy, delicate palette for white dashboard (never looks hard or harsh)
const DASHBOARD_ROCKET_TRAILS = [
  "#f472b6", // Soft Rose Pink
  "#38bdf8", // Sky Blue
  "#fbbf24", // Warm Butter Gold
  "#34d399", // Mint Green
  "#a78bfa", // Soft Lavender
  "#fb923c", // Warm Peach
];

const DASHBOARD_BURST_COLORS = [
  "#f472b6", // Soft Rose Pink
  "#38bdf8", // Soft Sky Blue
  "#fbbf24", // Warm Butter Gold
  "#34d399", // Soft Mint
  "#a78bfa", // Soft Lavender
  "#fb923c", // Soft Peach
  "#818cf8", // Soft Periwinkle
];

const randomBetween = (min, max) => min + Math.random() * (max - min);

function drawStar(context, x, y, radius, rotation) {
  context.beginPath();
  for (let point = 0; point < 10; point += 1) {
    const angle = rotation + (point * Math.PI) / 5;
    const pointRadius = point % 2 === 0 ? radius : radius * 0.43;
    const pointX = x + Math.cos(angle) * pointRadius;
    const pointY = y + Math.sin(angle) * pointRadius;
    if (point === 0) context.moveTo(pointX, pointY);
    else context.lineTo(pointX, pointY);
  }
  context.closePath();
  context.fill();
}

function drawDiya(context, centerX, centerY, time, side) {
  const width = 42;
  const flameFlicker = Math.sin(time * 0.012 + side * 1.7);
  const flameHeight = 17 + flameFlicker * 2.5;

  context.save();
  context.translate(centerX, centerY);

  // Warm radiating candlelight aura around Diya
  const diyaGlow = context.createRadialGradient(0, -6, 2, 0, -6, 28 + flameFlicker * 3);
  diyaGlow.addColorStop(0, "rgba(251, 191, 36, 0.45)");
  diyaGlow.addColorStop(0.5, "rgba(249, 115, 22, 0.16)");
  diyaGlow.addColorStop(1, "rgba(0, 0, 0, 0)");
  context.fillStyle = diyaGlow;
  context.beginPath();
  context.arc(0, -6, 28 + flameFlicker * 3, 0, TWO_PI);
  context.fill();

  const bowl = context.createLinearGradient(0, -4, 0, 12);
  bowl.addColorStop(0, "#fcd88a");
  bowl.addColorStop(0.35, "#c66b2b");
  bowl.addColorStop(1, "#713512");
  context.fillStyle = bowl;
  context.beginPath();
  context.moveTo(-width / 2, -3);
  context.quadraticCurveTo(-width / 2 + 3, 10, 0, 11);
  context.quadraticCurveTo(width / 2 - 3, 10, width / 2, -3);
  context.quadraticCurveTo(0, 2, -width / 2, -3);
  context.fill();

  context.strokeStyle = "#ffda8a";
  context.lineWidth = 1.3;
  context.beginPath();
  context.moveTo(-width / 2 + 2, -3);
  context.quadraticCurveTo(0, 2, width / 2 - 2, -3);
  context.stroke();

  const flame = context.createLinearGradient(0, 1, 0, -flameHeight);
  flame.addColorStop(0, "#fff7b2");
  flame.addColorStop(0.45, "#fbbf24");
  flame.addColorStop(1, "#f97316");
  context.fillStyle = flame;
  context.beginPath();
  context.moveTo(-5, 0);
  context.quadraticCurveTo(
    -8 + flameFlicker,
    -flameHeight * 0.7,
    flameFlicker * 2,
    -flameHeight,
  );
  context.quadraticCurveTo(
    8 - flameFlicker,
    -flameHeight * 0.65,
    5,
    0,
  );
  context.quadraticCurveTo(0, 4, -5, 0);
  context.fill();

  context.restore();
}

// Draw authentic illustrated Diwali Rocket as shown in User Image 1
function drawDiwaliRocket(context, rocket, isDashboard = false) {
  const { x, y, trailColor, vx = 0, vy = -7 } = rocket;

  context.save();
  context.translate(x, y);

  // Dynamic trajectory tilt angle so rockets arc and lean naturally
  const angle = Math.atan2(vy, vx) + Math.PI / 2;
  if (Math.abs(angle) > 0.001) {
    context.rotate(angle);
  }

  const flicker = Math.sin(Date.now() * 0.05 + x) * 2.5;
  const flameLength = 26 + flicker;
  const flameWidth = 7.5;

  // 1. Exhaust Flame & Colorful Tail (firing downwards from bottom)
  context.save();
  context.shadowColor = trailColor;
  context.shadowBlur = isDashboard ? 4 : 12;
  context.fillStyle = trailColor;
  context.beginPath();
  context.moveTo(-flameWidth / 2, 13);
  context.lineTo(-flameWidth / 2, 13 + flameLength - 4);
  context.quadraticCurveTo(0, 13 + flameLength + 4, flameWidth / 2, 13 + flameLength - 4);
  context.lineTo(flameWidth / 2, 13);
  context.closePath();
  context.fill();

  // Core bright inner flame
  context.fillStyle = "#fffbe6";
  context.beginPath();
  context.moveTo(-flameWidth / 4, 13);
  context.lineTo(-flameWidth / 4, 13 + flameLength * 0.48);
  context.quadraticCurveTo(0, 13 + flameLength * 0.48 + 3, flameWidth / 4, 13 + flameLength * 0.48);
  context.lineTo(flameWidth / 4, 13);
  context.closePath();
  context.fill();
  context.restore();

  // 2. Wooden Stick (pointing downwards)
  context.fillStyle = "#8d5524";
  context.fillRect(-1.2, 12, 2.4, 30);

  // 3. Cylindrical Red Rocket Body
  const bodyW = 13;
  const bodyH = 22;
  const bodyTop = -9;

  const bodyGrad = context.createLinearGradient(-bodyW / 2, 0, bodyW / 2, 0);
  bodyGrad.addColorStop(0, "#b92b27");
  bodyGrad.addColorStop(0.3, "#e74c3c");
  bodyGrad.addColorStop(0.7, "#ff6b6b");
  bodyGrad.addColorStop(1, "#b92b27");
  context.fillStyle = bodyGrad;
  context.fillRect(-bodyW / 2, bodyTop, bodyW, bodyH);

  // Golden Middle Band on Rocket Body
  const bandGrad = context.createLinearGradient(-bodyW / 2, 0, bodyW / 2, 0);
  bandGrad.addColorStop(0, "#d4ac0d");
  bandGrad.addColorStop(0.4, "#f1c40f");
  bandGrad.addColorStop(1, "#f39c12");
  context.fillStyle = bandGrad;
  context.fillRect(-bodyW / 2, bodyTop + 7, bodyW, 7);

  // 4. Yellow Conical Nose Cone (Tip)
  context.beginPath();
  context.moveTo(0, bodyTop - 14); // sharp tip
  context.lineTo(-bodyW / 2 - 1.5, bodyTop); // left corner
  context.lineTo(bodyW / 2 + 1.5, bodyTop); // right corner
  context.closePath();
  const coneGrad = context.createLinearGradient(-bodyW / 2, 0, bodyW / 2, 0);
  coneGrad.addColorStop(0, "#d4ac0d");
  coneGrad.addColorStop(0.35, "#f1c40f");
  coneGrad.addColorStop(0.8, "#f39c12");
  coneGrad.addColorStop(1, "#d4ac0d");
  context.fillStyle = coneGrad;
  context.fill();

  context.restore();
}

// Create Grand Chrysanthemum / Pearl Radial Burst as shown in User Image 2
function createGrandBurst(bursts, particles, foregroundParticles, x, y, customRadius, isDashboard = false) {
  // On dashboard: 28 airy rays so it never looks hard or congested
  const rayCount = isDashboard ? 28 : 50;
  const maxRadius = customRadius || (isDashboard ? randomBetween(80, 115) : randomBetween(100, 145));
  const rays = [];
  const palette = isDashboard ? DASHBOARD_BURST_COLORS : ORIGINAL_BURST_COLORS;

  for (let index = 0; index < rayCount; index += 1) {
    const angle = (index / rayCount) * TWO_PI + randomBetween(-0.035, 0.035);
    const color = palette[index % palette.length];
    const rayDist = maxRadius * randomBetween(0.85, 1.05);
    const speed = randomBetween(2.0, 3.2);

    rays.push({
      angle,
      color,
      maxDist: rayDist,
      currentDist: 0,
      speed,
    });
  }

  bursts.push({
    x,
    y,
    age: 0,
    duration: isDashboard ? 1.5 : 2.3, // Faster dissipation on dashboard so it never piles up
    maxDist: maxRadius,
    rays,
    haloRadius: 0,
    haloMax: maxRadius * 0.95,
  });

  // Embers: delicate, gentle sparkles on dashboard
  const emberCount = isDashboard ? 10 : 26;
  const embersPalette = isDashboard
    ? ["#fbbf24", "#f472b6", "#38bdf8", "#34d399", "#a78bfa"]
    : ["#ffffff", GOLD, "#fef08a"];

  for (let index = 0; index < emberCount; index += 1) {
    const angle = (index / emberCount) * TWO_PI + randomBetween(-0.12, 0.12);
    const speed = randomBetween(0.8, isDashboard ? 2.0 : 2.8);
    const targetPool = foregroundParticles && Math.random() < 0.35 && !isDashboard ? foregroundParticles : particles;
    targetPool.push({
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      gravity: 0.035,
      drag: 0.982,
      age: 0,
      life: isDashboard ? randomBetween(1.0, 1.6) : randomBetween(1.5, 2.4),
      size: isDashboard ? randomBetween(1.3, 2.0) : randomBetween(1.8, 3.2),
      color: embersPalette[index % embersPalette.length],
      kind: "ember",
    });
  }
}

function drawBursts(context, bursts, step, isDashboard = false) {
  for (let bIndex = bursts.length - 1; bIndex >= 0; bIndex -= 1) {
    const burst = bursts[bIndex];
    burst.age += step / 60;
    const progress = Math.min(1, burst.age / burst.duration);

    if (progress >= 1) {
      bursts.splice(bIndex, 1);
      continue;
    }

    const fade = progress < 0.65 ? 1 : Math.max(0, 1 - (progress - 0.65) / 0.35);

    context.save();
    context.globalAlpha = isDashboard ? fade * 0.82 : fade;

    // Expanding concentric shockwave halo ring as in Image 2
    if (burst.haloRadius < burst.haloMax) {
      burst.haloRadius += (burst.haloMax - burst.haloRadius) * 0.07 * step;
      const haloAlpha = Math.max(0, (1 - burst.haloRadius / burst.haloMax) * 0.38 * fade);
      if (haloAlpha > 0) {
        context.beginPath();
        context.strokeStyle = `rgba(251, 191, 36, ${haloAlpha})`;
        context.lineWidth = 1.2;
        context.arc(burst.x, burst.y, burst.haloRadius, 0, TWO_PI);
        context.stroke();
      }
    }

    // Radiating Chrysanthemum lines with bright white pearl tips as in Image 2
    const expandProgress = 1 - Math.pow(1 - Math.min(1, progress * 2.2), 3);

    for (let rIndex = 0; rIndex < burst.rays.length; rIndex += 1) {
      const ray = burst.rays[rIndex];
      const dist = ray.maxDist * expandProgress;
      const innerDist = dist * 0.18;

      const cosA = Math.cos(ray.angle);
      const sinA = Math.sin(ray.angle);

      const startX = burst.x + cosA * innerDist;
      const startY = burst.y + sinA * innerDist;
      const tipX = burst.x + cosA * dist;
      const tipY = burst.y + sinA * dist;

      // Draw the colored ray line
      context.beginPath();
      context.strokeStyle = ray.color;
      context.lineWidth = isDashboard ? 1.3 : 1.8;
      context.lineCap = "round";
      context.moveTo(startX, startY);
      context.lineTo(tipX, tipY);
      context.stroke();

      // Draw the crisp glowing pearl dot at ray tip (distinct Image 2 signature)
      const dotSize = Math.max(1, 2.5 * (1 - progress * 0.35));
      context.beginPath();
      context.fillStyle = isDashboard ? ray.color : "#ffffff";
      context.arc(tipX, tipY, isDashboard ? dotSize * 0.85 : dotSize, 0, TWO_PI);
      context.fill();
    }

    context.restore();
  }
}

function drawParticle(context, particle, step, isDashboard = false) {
  particle.age += step / 60;
  if (particle.age >= particle.life) return false;

  particle.vx *= Math.pow(particle.drag, step);
  particle.vy = particle.vy * Math.pow(particle.drag, step) + particle.gravity * step;
  particle.x += particle.vx * step;
  particle.y += particle.vy * step;

  const lifeProgress = particle.age / particle.life;
  context.globalAlpha = Math.max(0, 1 - lifeProgress);
  const renderColor = (isDashboard && (particle.color === "#ffffff" || particle.color === "#fef9c3"))
    ? "#fbbf24"
    : particle.color;
  context.fillStyle = renderColor;

  if (particle.kind === "star") {
    drawStar(
      context,
      particle.x,
      particle.y,
      particle.size * (1 - lifeProgress * 0.25),
      particle.age * 3,
    );
  } else {
    const radius = particle.size * (1 - lifeProgress * 0.35);
    context.beginPath();
    context.arc(particle.x, particle.y, Math.max(0.5, radius), 0, TWO_PI);
    context.fill();
  }

  return true;
}

function createAudio(context) {
  const masterGain = context.createGain();
  masterGain.gain.setValueAtTime(0.72, context.currentTime);
  masterGain.connect(context.destination);

  const sampleCount = Math.ceil(context.sampleRate * 0.75);
  const noiseBuffer = context.createBuffer(1, sampleCount, context.sampleRate);
  const noiseSamples = noiseBuffer.getChannelData(0);
  for (let index = 0; index < sampleCount; index += 1) {
    noiseSamples[index] = Math.random() * 2 - 1;
  }

  const playLaunch = () => {
    try {
      const now = context.currentTime;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = "triangle";
      oscillator.frequency.setValueAtTime(320, now);
      oscillator.frequency.exponentialRampToValueAtTime(1450, now + 0.65);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.12, now + 0.035);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.75);
      oscillator.connect(gain);
      gain.connect(masterGain);
      oscillator.start(now);
      oscillator.stop(now + 0.78);
    } catch (e) {}
  };

  const playBlast = () => {
    try {
      const now = context.currentTime;
      const boom = context.createOscillator();
      const boomGain = context.createGain();
      boom.type = "sine";
      boom.frequency.setValueAtTime(140 + Math.random() * 50, now);
      boom.frequency.exponentialRampToValueAtTime(26, now + 0.65);
      boomGain.gain.setValueAtTime(0.0001, now);
      boomGain.gain.exponentialRampToValueAtTime(0.48, now + 0.025);
      boomGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.72);
      boom.connect(boomGain);
      boomGain.connect(masterGain);
      boom.start(now);
      boom.stop(now + 0.74);

      const noise = context.createBufferSource();
      const lowpass = context.createBiquadFilter();
      const noiseGain = context.createGain();
      noise.buffer = noiseBuffer;
      lowpass.type = "lowpass";
      lowpass.frequency.setValueAtTime(1000 + Math.random() * 400, now);
      lowpass.frequency.exponentialRampToValueAtTime(120, now + 0.65);
      noiseGain.gain.setValueAtTime(0.0001, now);
      noiseGain.gain.exponentialRampToValueAtTime(0.16, now + 0.02);
      noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.7);
      noise.connect(lowpass);
      lowpass.connect(noiseGain);
      noiseGain.connect(masterGain);
      noise.start(now);
      noise.stop(now + 0.72);
    } catch (e) {}
  };

  return { playLaunch, playBlast };
}

export function DiwaliCelebrationOverlay({
  isActive,
  variant = "full",
  durationMs = null,
  onComplete,
}) {
  const canvasRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  const [isVisible, setIsVisible] = useState(isActive);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const isFadingOutRef = useRef(false);

  onCompleteRef.current = onComplete;

  const handleClose = () => {
    if (isFadingOutRef.current) return;
    isFadingOutRef.current = true;
    setIsFadingOut(true);
    setTimeout(() => {
      setIsVisible(false);
      onCompleteRef.current?.();
    }, 1400);
  };


  useEffect(() => {
    if (!isActive || !isVisible || Date.now() > EXPIRY_DATE || !durationMs) return;

    const fadeMs = Math.min(1400, Math.floor(durationMs * 0.2));
    const fadeTimer = setTimeout(() => {
      if (!isFadingOutRef.current) {
        isFadingOutRef.current = true;
        setIsFadingOut(true);
      }
    }, Math.max(100, durationMs - fadeMs));

    const completeTimer = setTimeout(() => {
      setIsVisible(false);
      onCompleteRef.current?.();
    }, durationMs);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(completeTimer);
    };
  }, [isActive, isVisible, durationMs]);

  useEffect(() => {
    if (!isActive || Date.now() >= EXPIRY_DATE) {
      setIsVisible(false);
      if (isActive) onCompleteRef.current?.();
      return;
    }

    setIsVisible(true);
    setIsFadingOut(false);
    isFadingOutRef.current = false;
  }, [isActive]);

  useEffect(() => {
    if (!isActive || !isVisible || Date.now() > EXPIRY_DATE) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isActive, isVisible]);

  useEffect(() => {
    if (!isActive || !isVisible || Date.now() >= EXPIRY_DATE) return;

    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) {
      console.error("Diwali celebration could not initialize its canvas.");
      setIsVisible(false);
      onCompleteRef.current?.();
      return;
    }

    let width = window.innerWidth;
    let height = window.innerHeight;
    let animationFrame = 0;
    let completed = false;
    let nextRandomLaunchTime = variant === "dashboard" ? 350 : 1600;
    let previousFrameTime = 0;
    const startTime = performance.now();

    const rockets = [];
    const bursts = [];
    const particles = [];
    const foregroundParticles = [];

    // Pre-generate subtle cosmic twinkling stars across background as in Image 2
    const bgStars = [];
    for (let i = 0; i < 45; i += 1) {
      bgStars.push({
        x: Math.random() * width,
        y: Math.random() * height * 0.85,
        size: randomBetween(0.8, 2.2),
        phase: Math.random() * 10,
      });
    }

    let sounds;
    let audioContext;
    let skyFlash = 0;

    const resizeCanvas = () => {
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.round(width * pixelRatio);
      canvas.height = Math.round(height * pixelRatio);
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    if (typeof window.AudioContext === "function") {
      try {
        audioContext = new window.AudioContext();
        sounds = createAudio(audioContext);
        if (audioContext.state === "suspended") {
          const resumeAudio = () => {
            if (audioContext && audioContext.state === "suspended") {
              void audioContext.resume().catch(() => {});
            }
          };
          window.addEventListener("pointerdown", resumeAudio, { once: true });
          window.addEventListener("keydown", resumeAudio, { once: true });
        }
      } catch (error) {}
    }

    const launchSound = () => sounds?.playLaunch();
    const blastSound = () => sounds?.playBlast();

    const handleVisibility = () => {
      if (document.hidden) {
        previousFrameTime = 0;
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    // Launch Wave 1: Diwali Rockets across full screen width (cascading organic heights - no flat line)
    const launchWaveOne = () => {
      const rocketCount = isDashboard ? (width >= 1200 ? 7 : 5) : (width >= 1200 ? 10 : 8);
      for (let index = 0; index < rocketCount; index += 1) {
        const x = ((index + 0.5) / rocketCount) * width;
        const centerOffset = (x - width / 2) / (width / 2);
        const vx = centerOffset * randomBetween(0.3, 0.7) + randomBetween(-0.2, 0.2);

        // Dynamic 3-tier staggered explosion heights (high sky, mid sky, crown)
        let targetY;
        const tier = index % 3;
        if (tier === 0) {
          targetY = height * randomBetween(0.12, 0.18); // High sky canopy
        } else if (tier === 1) {
          targetY = height * randomBetween(0.24, 0.32); // Mid-high canopy
        } else {
          targetY = height * randomBetween(0.36, 0.45); // Deep sky arch
        }

        // Staggered launch depths and speeds so they rise organically at varied rates
        const launchOffset = (index % 4) * 24 + randomBetween(0, 35);
        const vy = -(isDashboard ? randomBetween(8.5, 12.0) : randomBetween(6.8, 9.2));

        rockets.push({
          x,
          y: height + 20 + launchOffset,
          vx,
          vy,
          targetY,
          burstRadius: randomBetween(110, 150),
          trailColor: (isDashboard ? DASHBOARD_ROCKET_TRAILS : ORIGINAL_ROCKET_TRAILS)[index % (isDashboard ? DASHBOARD_ROCKET_TRAILS : ORIGINAL_ROCKET_TRAILS).length],
        });
        launchSound();
      }
    };

    // Phase 2: Dynamic multi-rocket waves with fully randomized, multi-tier heights across the entire screen
    const launchRandomDiwaliBatch = () => {
      const isWide = width >= 1200;
      const isMedium = width >= 800;
      // On dashboard: launch only 2-3 gentle rockets so it never looks hard or crowded
      const minBatch = isDashboard ? 4 : (isWide ? 5 : isMedium ? 4 : 3);
      const maxBatch = isDashboard ? 6 : (isWide ? 8 : isMedium ? 6 : 5);
      const batchSize = Math.floor(randomBetween(minBatch, maxBatch + 0.99));
      const trailPalette = isDashboard ? DASHBOARD_ROCKET_TRAILS : ORIGINAL_ROCKET_TRAILS;

      const titleY = height * 0.58; // level where HAPPY DIWALI is displayed

      // Distribute rockets across slots across the screen width
      for (let r = 0; r < batchSize; r += 1) {
        const slotWidth = width / batchSize;
        const x = slotWidth * r + randomBetween(slotWidth * 0.12, slotWidth * 0.88);

        // Natural outward arc trajectory based on screen position
        const centerOffset = (x - width / 2) / (width / 2);
        const vx = centerOffset * randomBetween(0.35, 1.15) + randomBetween(-0.35, 0.35);

        // Rich Multi-Tier Heights: completely breaks any single horizontal line!
        // 1. High Sky Canopy (Top): 25% rockets soar high into upper canopy
        // 2. Mid Sky (Overhead): 30% rockets arch across upper-mid sky
        // 3. Center Framing: 25% rockets explode near Happy Diwali level
        // 4. Low-Mid Wings (Sides): 20% rockets explode in lower side flanks
        let targetY;
        const distFromCenter = Math.abs(x - width / 2);
        const heightRoll = Math.random();

        if (distFromCenter < width * 0.26) {
          // Rockets near center horizontally: burst either in high sky or crowning arch above text
          if (heightRoll < 0.45) {
            targetY = height * randomBetween(0.12, 0.22); // High sky canopy
          } else {
            targetY = titleY - randomBetween(75, 175); // Crowning arch overhead
          }
        } else {
          // Rockets on the sides (left & right wings): varied top, mid, and low flank bursts
          if (heightRoll < 0.25) {
            targetY = height * randomBetween(0.14, 0.25); // High sky wing
          } else if (heightRoll < 0.60) {
            targetY = titleY - randomBetween(30, 130); // Mid sky wing
          } else if (heightRoll < 0.85) {
            targetY = titleY + randomBetween(-30, 45); // Greeting level flank
          } else {
            targetY = titleY + randomBetween(50, 100); // Lower festive flank
          }
        }

        // Varied burst radius based on elevation
        const burstRadius = targetY < height * 0.35
          ? randomBetween(105, 150) // bigger in the open upper sky
          : randomBetween(85, 125);  // compact and detailed near the mid/lower sky

        // Staggered launch start and randomized speeds so rockets rise organically at different times
        const launchOffset = r * 18 + randomBetween(0, 45);
        const vy = -(isDashboard ? randomBetween(8.5, 12.2) : randomBetween(6.4, 9.2));

        rockets.push({
          x,
          y: height + 20 + launchOffset,
          vx,
          vy,
          targetY,
          burstRadius,
          trailColor: trailPalette[Math.floor(Math.random() * trailPalette.length)],
        });
      }

      launchSound();
    };

    launchWaveOne();

    const drawGreeting = (elapsed, alpha) => {
      if (elapsed < 900 || alpha <= 0) return;

      const centerX = width / 2;
      const centerY = height * 0.58;
      const titleFontSize = Math.max(38, Math.min(86, width * 0.075));
      const titleY = centerY;
      const title = "HAPPY DIWALI";

      context.save();
      context.globalAlpha = alpha;
      context.textAlign = "center";
      context.textBaseline = "middle";

      // Pre-measure title width for sizing contrast backdrop
      context.font = `900 ${titleFontSize}px Georgia, "Times New Roman", serif`;
      const titleWidth = context.measureText(title).width;

      // 1. Soft contrast vignette shield behind greeting
      const shieldRadiusX = Math.min(width * 0.48, Math.max(titleWidth * 0.78, 420));
      const shieldRadiusY = Math.min(height * 0.32, titleFontSize * 3.2);
      const shield = context.createRadialGradient(
        centerX,
        titleY,
        15,
        centerX,
        titleY,
        shieldRadiusX,
      );
      shield.addColorStop(0, "rgba(6, 4, 14, 0.52)");
      shield.addColorStop(0.55, "rgba(8, 5, 18, 0.32)");
      shield.addColorStop(1, "rgba(0, 0, 0, 0)");
      context.fillStyle = shield;
      context.beginPath();
      context.ellipse(centerX, titleY, shieldRadiusX, shieldRadiusY, 0, 0, TWO_PI);
      context.fill();

      // 2. Radiant divine warm golden backlight aura with gentle living candlelight shimmer
      const auraBreath = 1 + Math.sin(elapsed * 0.003) * 0.06;
      const glowRadius = Math.min(shieldRadiusX * 0.82, 320) * auraBreath;
      const centerGlow = context.createRadialGradient(
        centerX,
        titleY,
        10,
        centerX,
        titleY,
        glowRadius,
      );
      centerGlow.addColorStop(0, "rgba(251, 191, 36, 0.35)");
      centerGlow.addColorStop(0.5, "rgba(245, 158, 11, 0.12)");
      centerGlow.addColorStop(1, "rgba(0, 0, 0, 0)");
      context.fillStyle = centerGlow;
      context.beginPath();
      context.arc(centerX, titleY, glowRadius, 0, TWO_PI);
      context.fill();

      // Subtitle 1: Festival of Lights
      const sub1Size = Math.max(14, Math.min(22, width * 0.024));
      context.font = `700 ${sub1Size}px Georgia, serif`;
      context.shadowColor = "rgba(0, 0, 0, 0.95)";
      context.shadowBlur = 10;
      context.shadowOffsetY = 2;
      context.fillStyle = "#fef08a";
      context.fillText(
        "✨ शुभ दीपावली • FESTIVAL OF LIGHTS ✨",
        centerX,
        titleY - titleFontSize * 1.25,
        width - 32,
      );

      // Main Title: HAPPY DIWALI in ultra-crisp 24K Gold with dark separation outline
      context.font = `900 ${titleFontSize}px Georgia, "Times New Roman", serif`;

      // Pass 1: Dark separation stroke & drop shadow (makes letters pop out with 100% clarity)
      context.strokeStyle = "rgba(6, 4, 12, 0.96)";
      context.lineWidth = Math.max(4, titleFontSize * 0.08);
      context.lineJoin = "round";
      context.miterLimit = 2;
      context.shadowColor = "rgba(0, 0, 0, 0.98)";
      context.shadowBlur = 18;
      context.shadowOffsetY = 4;
      context.strokeText(title, centerX, titleY, width - 24);

      // Pass 2: Luminous 24K Royal Gold Gradient Fill with divine golden bloom
      const titleGradient = context.createLinearGradient(
        0,
        titleY - titleFontSize * 0.55,
        0,
        titleY + titleFontSize * 0.55,
      );
      titleGradient.addColorStop(0, "#ffffff"); // glistening white-gold bright highlight on top
      titleGradient.addColorStop(0.24, "#fef08a"); // bright golden sheen
      titleGradient.addColorStop(0.58, "#fbbf24"); // rich gold
      titleGradient.addColorStop(0.85, "#f59e0b"); // warm amber
      titleGradient.addColorStop(1, "#d97706"); // deep royal gold at base
      context.fillStyle = titleGradient;
      context.shadowColor = "rgba(251, 191, 36, 0.95)";
      context.shadowBlur = 26;
      context.shadowOffsetY = 0;
      context.fillText(title, centerX, titleY, width - 24);
      context.shadowBlur = 0;

      // Cinematic diamond sparkle glint catching the golden letters periodically
      const glintCycle = (elapsed * 0.0007) % 1;
      if (glintCycle < 0.38) {
        const glintProgress = glintCycle / 0.38;
        const glintAlpha = Math.sin(glintProgress * Math.PI);
        const glintSlot = (Math.floor(elapsed * 0.0007) * 0.38) % 1;
        const glintX = centerX - titleWidth * 0.42 + titleWidth * 0.84 * glintSlot;
        const glintY = titleY - titleFontSize * 0.28;
        const glintSize = Math.max(3.5, titleFontSize * 0.13) * glintAlpha;

        context.save();
        context.globalAlpha = alpha * glintAlpha;
        context.fillStyle = "#ffffff";
        context.shadowColor = "#fde047";
        context.shadowBlur = 12;
        context.beginPath();
        context.moveTo(glintX, glintY - glintSize);
        context.lineTo(glintX + glintSize * 0.28, glintY);
        context.lineTo(glintX + glintSize, glintY);
        context.lineTo(glintX + glintSize * 0.28, glintY);
        context.lineTo(glintX, glintY + glintSize);
        context.lineTo(glintX - glintSize * 0.28, glintY);
        context.lineTo(glintX - glintSize, glintY);
        context.lineTo(glintX - glintSize * 0.28, glintY);
        context.closePath();
        context.fill();
        context.restore();
      }

      // Diyas on left and right
      const diyaY = titleY + 2;
      const diyaOffset = Math.min(titleWidth / 2 + 34, width * 0.44);
      drawDiya(context, centerX - diyaOffset, diyaY, elapsed, -1);
      drawDiya(context, centerX + diyaOffset, diyaY, elapsed, 1);

      // Subtitle 2: Shloka in quotation marks
      const sub2Size = Math.max(13, Math.min(19, width * 0.020));
      context.font = `600 ${sub2Size}px Georgia, serif`;
      context.shadowColor = "rgba(0, 0, 0, 0.95)";
      context.shadowBlur = 10;
      context.shadowOffsetY = 2;
      context.fillStyle = "#fff7ed";
      context.fillText(
        "“तमसो मा ज्योतिर्गमय — May divine lights illuminate your path”",
        centerX,
        titleY + titleFontSize * 0.95,
        width - 32,
      );

      // Subtitle 3: Festive blessings & team wishes
      const sub3Size = Math.max(12, Math.min(17, width * 0.017));
      context.font = `500 ${sub3Size}px Georgia, "Times New Roman", serif`;
      context.shadowColor = "rgba(0, 0, 0, 0.95)";
      context.shadowBlur = 8;
      context.shadowOffsetY = 1;
      context.fillStyle = "#ffedd5";
      context.fillText(
        "Wishing you and your family joy, health and prosperity.",
        centerX,
        titleY + titleFontSize * 1.42,
        width - 36,
      );
      context.fillText(
        "May your home and your team be filled with light, joy and prosperity.",
        centerX,
        titleY + titleFontSize * 1.76,
        width - 36,
      );

      context.restore();
    };

    const renderFrame = (now) => {
      const elapsed = now - startTime;
      const isDashboard = variant === "dashboard";
      if (Date.now() > EXPIRY_DATE) {
        context.clearRect(0, 0, width, height);
        completed = true;
        setIsVisible(false);
        onCompleteRef.current?.();
        return;
      }

      const step = previousFrameTime
        ? Math.min((now - previousFrameTime) / (1000 / 60), 2)
        : 1;
      previousFrameTime = now;
      context.clearRect(0, 0, width, height);

      // Phase 2: Dynamic Continuous Grand Fireworks Show filling the entire screen continuously until closed
      if (elapsed >= nextRandomLaunchTime) {
        launchRandomDiwaliBatch();
        nextRandomLaunchTime = elapsed + (isDashboard ? randomBetween(450, 750) : randomBetween(420, 680));
      }

      context.save();

      // Subtle sky illumination flash on firework blasts (cinematic realism)
      if (!isDashboard && skyFlash > 0.005) {
        context.fillStyle = `rgba(251, 191, 36, ${skyFlash * 0.16})`;
        context.fillRect(0, 0, width, height);
        skyFlash *= 0.88;
      }

      // 1. Draw subtle background twinkling stars as in User Image 2
      if (!isDashboard) {
        for (let s = 0; s < bgStars.length; s += 1) {
        const star = bgStars[s];
        const starAlpha = 0.35 + 0.45 * Math.sin(now * 0.003 + star.phase);
        if (starAlpha > 0) {
          context.fillStyle = `rgba(255, 255, 255, ${starAlpha})`;
          context.beginPath();
          context.arc(star.x, star.y, star.size, 0, TWO_PI);
          context.fill();
        }
      }
      }

      // Keep memory strictly bounded: fewer items on dashboard keeps screen light and spacious
      if (isDashboard) {
        if (particles.length > 200) particles.splice(0, particles.length - 200);
        if (bursts.length > 24) bursts.splice(0, bursts.length - 24);
        if (rockets.length > 18) rockets.splice(0, rockets.length - 18);
      } else {
        if (particles.length > 280) particles.splice(0, particles.length - 280);
        if (foregroundParticles.length > 120) foregroundParticles.splice(0, foregroundParticles.length - 120);
        if (bursts.length > 32) bursts.splice(0, bursts.length - 32);
        if (rockets.length > 24) rockets.splice(0, rockets.length - 24);
      }

      // 2. Update & Draw Rockets (Image 1 design)
      for (let index = rockets.length - 1; index >= 0; index -= 1) {
        const rocket = rockets[index];
        rocket.x += (rocket.vx || 0) * step;
        rocket.y += rocket.vy * step;

        // Sparkle stars falling from rocket tail
        if (Math.random() < 0.75) {
          particles.push({
            x: rocket.x + randomBetween(-3, 3),
            y: rocket.y + 36,
            vx: randomBetween(-0.8, 0.8) - (rocket.vx || 0) * 0.25,
            vy: randomBetween(1.2, 2.8),
            gravity: 0.04,
            drag: 0.98,
            age: 0,
            life: randomBetween(0.45, 0.9),
            size: randomBetween(1.8, 3.2),
            color: rocket.trailColor,
            kind: "star",
          });
        }

        // Rocket reaches its explosion apex -> Burst into Grand Chrysanthemum Starburst (Image 2)
        if (rocket.y <= rocket.targetY) {
          skyFlash = Math.min(0.24, skyFlash + 0.08);
          createGrandBurst(bursts, particles, foregroundParticles, rocket.x, rocket.y, rocket.burstRadius, isDashboard);
          blastSound();
          rockets.splice(index, 1);
          continue;
        }

        drawDiwaliRocket(context, rocket, isDashboard);
      }

      // Continuous golden fire sparks and embers floating directly IN FRONT OF the text
      if (!isDashboard && elapsed >= 950) {
        const titleY = height * 0.58;
        const centerX = width / 2;

        if (Math.random() < 0.65) {
          const spawnX = centerX + randomBetween(-width * 0.38, width * 0.38);
          const spawnY = titleY - randomBetween(50, 160);
          foregroundParticles.push({
            x: spawnX,
            y: spawnY,
            vx: randomBetween(-0.6, 0.6),
            vy: randomBetween(0.8, 2.2),
            gravity: 0.025,
            drag: 0.985,
            age: 0,
            life: randomBetween(1.5, 2.4),
            size: randomBetween(1.6, 3.0),
            color: ["#ffffff", "#fef08a", "#fecdd3", "#bae6fd", "#bbf7d0", "#ddd6fe", "#fed7aa", "#fbcfe8"][Math.floor(Math.random() * 8)],
            kind: Math.random() < 0.5 ? "star" : "ember",
          });
        }

        // Tiny dancing flame sparks rising from left and right Diyas
        if (Math.random() < 0.45) {
          const titleWidthApprox = Math.min(width * 0.75, 520);
          const diyaOffset = titleWidthApprox / 2 + 34;
          const leftOrRight = Math.random() < 0.5 ? -1 : 1;
          foregroundParticles.push({
            x: centerX + leftOrRight * diyaOffset + randomBetween(-3, 3),
            y: titleY - 6 + randomBetween(-2, 2),
            vx: leftOrRight * randomBetween(0.1, 0.5) + randomBetween(-0.3, 0.3),
            vy: -randomBetween(0.8, 1.8),
            gravity: -0.012,
            drag: 0.97,
            age: 0,
            life: randomBetween(0.6, 1.1),
            size: randomBetween(1.2, 2.2),
            color: Math.random() < 0.5 ? "#fff7b2" : "#f97316",
            kind: "star",
          });
        }
      }

      // 3. Draw Grand Full-Screen Chrysanthemum Bursts (Image 2 design)
      drawBursts(context, bursts, step, isDashboard);

      // 4. Draw falling sparkle particles & embers (behind text)
      for (let index = particles.length - 1; index >= 0; index -= 1) {
        if (!drawParticle(context, particles[index], step, isDashboard)) {
          particles.splice(index, 1);
        }
      }

      // 5. Draw Festive Greetings Text
      if (!isDashboard) {
        const greetingAlpha =
          elapsed < 900
            ? 0
            : Math.min(1, (elapsed - 900) / 360);
        drawGreeting(elapsed, greetingAlpha);
      }

      // 6. Draw Foreground Fire Embers & Sparkles ON TOP OF / IN FRONT OF text!
      for (let index = foregroundParticles.length - 1; index >= 0; index -= 1) {
        if (!drawParticle(context, foregroundParticles[index], step, isDashboard)) {
          foregroundParticles.splice(index, 1);
        }
      }

      context.restore();

      animationFrame = window.requestAnimationFrame(renderFrame);
    };

    animationFrame = window.requestAnimationFrame(renderFrame);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", resizeCanvas);
      document.removeEventListener("visibilitychange", handleVisibility);
      if (!completed) context.clearRect(0, 0, width, height);
      if (audioContext && audioContext.state !== "closed") {
        void audioContext.close();
      }
    };
  }, [isActive, isVisible]);

  if (!isActive || !isVisible || Date.now() > EXPIRY_DATE) return null;

  const isDashboard = variant === "dashboard";

  return (
    <div
      className={`dco-celebration-wrapper ${isDashboard ? "dco-dashboard-mode" : ""} ${isFadingOut ? "dco-fading-out" : ""}`}
      role={isDashboard ? "presentation" : "dialog"}
      aria-modal={!isDashboard}
      aria-label="Diwali Celebration"
    >
      <canvas
        ref={canvasRef}
        className="dco-diwali-canvas-overlay"
        aria-hidden="true"
      />
      {!isDashboard && (
        <button
          className="dco-close-button"
          type="button"
          aria-label="Close Diwali celebration"
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
        >
          <span>✕ Close</span>
        </button>
      )}
    </div>
  );
}
